import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { forecastMath } from 'utils/fcMath'
import { parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { round } = forecastMath

export interface GeckoNozzleJSON {
  type: GeckoFeatureModelType.Nozzle
  name: string
  surface_specs: {
    thickness: number | undefined
  }
  feature_geometry: {
    x: number
    y: number
    radius: number
  }
  notes?: string
}

export const NewGeckoNozzleJSON: GeckoNozzleJSON = {
  type: GeckoFeatureModelType.Nozzle,
  name: 'New Nozzle',
  surface_specs: {
    thickness: undefined,
  },
  feature_geometry: {
    x: 0,
    y: 0,
    radius: 0,
  },
  notes: '',
}

export class GeckoNozzle extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.Nozzle
  readonly geometry: GeckoComponentCoords
  readonly radius: GeckoMeasurement
  readonly thickness: GeckoMeasurement | undefined
  readonly notes: string

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentCoords,
    radius: GeckoMeasurement,
    thickness: GeckoMeasurement | undefined,
    notes: string
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.radius = radius
    this.thickness = thickness
    this.notes = notes
  }

  public static CreateFromJSON(
    componentID: string,
    nozzleID: string,
    nozzleJSON: GeckoNozzleJSON
  ): GeckoNozzle {
    const featureGeometryData = nozzleJSON.feature_geometry as Record<
      string,
      number
    >
    const nozzleGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )
    const nozzleRadius = GeckoMeasurement.CreateFromValue(
      featureGeometryData.radius as number,
      GeckoMeasurementUnit.Meters
    )

    const surfaceSpecs = nozzleJSON.surface_specs as Record<string, number>
    const nozzleThickness =
      surfaceSpecs.thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.thickness as number,
            GeckoMeasurementUnit.Meters
          )
        : undefined

    const nozzleName = parseString(nozzleJSON.name) ?? ''
    const nozzleNotes = parseString(nozzleJSON.notes) ?? ''

    return new GeckoNozzle(
      nozzleID,
      nozzleName,
      nozzleGeometry,
      nozzleRadius,
      nozzleThickness,
      nozzleNotes
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoNozzleJSON>
  ): Map<string, GeckoNozzle> {
    return Object.keys(recordsJSON).reduce((resultObj, nozzleID) => {
      resultObj.set(
        nozzleID,
        GeckoNozzle.CreateFromJSON(componentID, nozzleID, recordsJSON[nozzleID])
      )
      return resultObj
    }, new Map<string, GeckoNozzle>())
  }

  public asJSON(): GeckoNozzleJSON {
    return generateNozzle(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.radius,
      this.thickness,
      this.notes
    )
  }
}

export function generateNozzle(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  radius: GeckoMeasurement,
  thickness: GeckoMeasurement | undefined,
  notes: string
): GeckoNozzleJSON {
  const nozzleJSON: GeckoNozzleJSON = {
    type: GeckoFeatureModelType.Nozzle,
    name: name,
    feature_geometry: {
      x: round(x.toNumber(GeckoMeasurementUnit.Meters), 5),
      y: round(y.toNumber(GeckoMeasurementUnit.Meters), 5),
      radius: round(radius.toNumber(GeckoMeasurementUnit.Meters), 5),
    },
    surface_specs: {
      thickness:
        thickness !== undefined
          ? thickness.toNumber(GeckoMeasurementUnit.Meters)
          : undefined,
    },
    notes: notes,
  }

  return nozzleJSON
}

export const NozzleFormFields = (nozzleJSON: GeckoNozzleJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Nozzle Name',
      value: nozzleJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: nozzleJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: nozzleJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: nozzleJSON.feature_geometry.radius * 2,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'thickness',
      label: 'Thickness',
      value: nozzleJSON.surface_specs.thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'notes',
      label: 'Notes',
      value: nozzleJSON.notes,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

export const NozzleJSONFromFormFields = (formFields: FormField[]) => {
  const nozzleThickness = getFormFieldByDataKey(formFields, 'thickness')
  const nozzleNotes = getFormFieldByDataKey(formFields, 'notes')
  return generateNozzle(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    nozzleThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          nozzleThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    nozzleNotes?.value as string
  )
}

export const NozzleFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.Nozzle,
  title: 'Nozzles',
  singularName: 'Nozzle',
  fields: ['name', 'x', 'y', 'radius', 'notes'],
}
